import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiLink, FiShield } from "react-icons/fi";

import api from "../../services/api";
import useStore from "../../services/store";
import Loader from "../../components/loader";
import LoadingButton from "../../components/loading-button";

const REQUIRED_PARAMS = ["client_id", "redirect_uri", "code_challenge", "response_type"];

export default function OauthAuthorize() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, setUser, setOrganization } = useStore();

  const [checkingSession, setCheckingSession] = useState(!user);
  const [client, setClient] = useState(null);
  const [clientError, setClientError] = useState(null);
  const [approving, setApproving] = useState(false);
  const [denying, setDenying] = useState(false);

  const params = Object.fromEntries(searchParams.entries());

  useEffect(() => {
    if (user) {
      setCheckingSession(false);
      return;
    }
    (async () => {
      try {
        const { ok, token, user: fetched, organisations } = await api.get("/user/signin_token");
        if (!ok || !fetched) {
          const back = window.location.pathname + window.location.search;
          navigate(`/auth?redirect=${encodeURIComponent(back)}`, { replace: true });
          return;
        }
        if (token) api.setToken(token);
        setUser(fetched);
        setOrganization(organisations?.[0] || null);
      } catch {
        const back = window.location.pathname + window.location.search;
        navigate(`/auth?redirect=${encodeURIComponent(back)}`, { replace: true });
      } finally {
        setCheckingSession(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!user || !params.client_id) return;
    (async () => {
      try {
        const res = await api.get(`/oauth/authorize/client?client_id=${encodeURIComponent(params.client_id)}`);
        if (res.error) {
          setClientError(res.error_description || "Unknown client");
          return;
        }
        setClient(res);
      } catch (e) {
        setClientError(e?.error_description || "Could not load client info");
      }
    })();
  }, [user, params.client_id]);

  const missingParams = REQUIRED_PARAMS.filter((k) => !params[k]);
  const invalidMethod = params.code_challenge_method && params.code_challenge_method !== "S256";
  const invalidResponseType = params.response_type && params.response_type !== "code";

  async function handleApprove() {
    try {
      setApproving(true);
      const res = await api.post("/oauth/authorize/grant", {
        client_id: params.client_id,
        redirect_uri: params.redirect_uri,
        code_challenge: params.code_challenge,
        code_challenge_method: params.code_challenge_method || "S256",
        response_type: params.response_type,
        state: params.state,
      });
      if (res.redirect_url) {
        window.location.href = res.redirect_url;
      } else {
        toast.error(res.error_description || "Authorization failed");
        setApproving(false);
      }
    } catch (e) {
      toast.error(e?.error_description || "Authorization failed");
      setApproving(false);
    }
  }

  async function handleDeny() {
    try {
      setDenying(true);
      const res = await api.post("/oauth/authorize/deny", {
        client_id: params.client_id,
        redirect_uri: params.redirect_uri,
        state: params.state,
      });
      if (res.redirect_url) {
        window.location.href = res.redirect_url;
      } else {
        toast.error(res.error_description || "Could not cancel");
        setDenying(false);
      }
    } catch (e) {
      toast.error(e?.error_description || "Could not cancel");
      setDenying(false);
    }
  }

  if (checkingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader />
      </div>
    );
  }

  if (missingParams.length || invalidMethod || invalidResponseType) {
    return (
      <Wrapper>
        <Header />
        <h1 className="text-xl font-semibold text-gray-900 mb-3">Requête OAuth invalide</h1>
        <p className="text-sm text-gray-600 mb-6">
          {missingParams.length
            ? `Paramètres manquants : ${missingParams.join(", ")}`
            : invalidMethod
            ? "Seule la méthode PKCE S256 est supportée."
            : "Seul response_type=code est supporté."}
        </p>
      </Wrapper>
    );
  }

  if (clientError) {
    return (
      <Wrapper>
        <Header />
        <h1 className="text-xl font-semibold text-gray-900 mb-3">Client inconnu</h1>
        <p className="text-sm text-gray-600">{clientError}</p>
      </Wrapper>
    );
  }

  if (!client) {
    return (
      <Wrapper>
        <div className="flex justify-center py-10">
          <Loader />
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Header />

      <div className="flex items-center justify-center gap-4 mb-6">
        <Logo src={client.logo_uri} alt={client.client_name} />
        <FiLink className="w-5 h-5 text-gray-400" />
        <JobegoMark />
      </div>

      <h1 className="text-xl font-semibold text-gray-900 text-center mb-2">
        Connecter <span className="text-primary">{client.client_name}</span> à Jobego
      </h1>
      <p className="text-sm text-gray-600 text-center mb-6">
        {client.client_name} pourra lire et gérer vos offres, candidatures, candidats et conversations en votre nom.
      </p>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 flex items-start gap-3">
        <FiShield className="w-5 h-5 text-gray-500 mt-0.5 shrink-0" />
        <div className="text-sm text-gray-700">
          Connecté en tant que <span className="font-medium text-gray-900">{user.firstname} {user.lastname}</span>
          <div className="text-xs text-gray-500">{user.email}</div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <LoadingButton
          loading={approving}
          disabled={denying}
          className="btn btn-primary btn-large w-full"
          onClick={handleApprove}
        >
          Autoriser
        </LoadingButton>
        <LoadingButton
          loading={denying}
          disabled={approving}
          className="btn btn-secondary btn-large w-full"
          onClick={handleDeny}
        >
          Annuler
        </LoadingButton>
      </div>

      <p className="text-xs text-gray-400 text-center mt-6">
        Vous pourrez révoquer cette autorisation à tout moment depuis vos réglages.
      </p>
    </Wrapper>
  );
}

function Wrapper({ children }) {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="relative max-w-md w-full px-4">
        <div className="w-full bg-white rounded-2xl shadow-lg border border-gray-200 p-6">{children}</div>
      </div>
    </div>
  );
}

function Header() {
  return (
    <div className="mb-6 flex justify-center">
      <span className="text-xl font-extrabold text-gray-900">Jobego</span>
    </div>
  );
}

function JobegoMark() {
  return (
    <div className="w-12 h-12 rounded-xl bg-primary text-white flex items-center justify-center text-sm font-extrabold">
      J
    </div>
  );
}

function Logo({ src, alt }) {
  if (!src) {
    return (
      <div className="w-12 h-12 rounded-xl bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-500 text-sm font-semibold">
        {(alt || "?").slice(0, 1).toUpperCase()}
      </div>
    );
  }
  return <img src={src} alt={alt} className="w-12 h-12 rounded-xl object-contain border border-gray-200 bg-white" />;
}
